import { Box, Button, Grid, Paper, TextField, Typography } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import { addToast } from '@sophocles/features/toast';
import { createContact, editContact, loadContacts } from '@sophocles/features/warehouse/orders/api';
import { applyApiErrorToFormik } from '@sophocles/utils/apiErrors';

// Form field names, used to attribute API validation errors to their input;
// anything the backend reports outside this list downgrades to the banner.
const CONTACT_FIELDS = ['name', 'phone1', 'phone2', 'email', 'address', 'state', 'city', 'zipcode', 'title'];

const ContactSchema = Yup.object().shape({
  name: Yup.string().max(60, 'Too Long!').required('Required'),
  phone1: Yup.string().max(80, 'Too Long!').nullable(),
  phone2: Yup.string().max(80, 'Too Long!').nullable(),
  email: Yup.string().email('Invalid email').max(60, 'Too Long!').required('Email is required'),
  address: Yup.string().max(100, 'Too Long!').nullable(),
  state: Yup.string().max(10, 'Too Long!').nullable(),
  city: Yup.string().max(20, 'Too Long!').nullable(),
  zipcode: Yup.string().max(15, 'Too Long!').nullable(),
  title: Yup.string().max(40, 'Too Long!').nullable(),
  customer: Yup.number().nullable(),
  site_group: Yup.number().nullable(),
});

function ContactForm({ contact, customerId = null, siteGroupId = null, onClose }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [nonFieldMessages, setNonFieldMessages] = React.useState([]);

  const initialValues = contact || {
    name: '',
    phone1: '',
    phone2: '',
    email: '',
    address: '',
    state: '',
    city: '',
    zipcode: '',
    title: '',
    customer: customerId,
    site_group: siteGroupId,
  };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setNonFieldMessages([]);
    try {
      if (contact) {
        await dispatch(editContact({ id: contact.id, ...values })).unwrap();
      } else {
        await dispatch(createContact({ ...values })).unwrap();
      }
      dispatch(loadContacts());
      onClose();
    } catch (error) {
      const messages = applyApiErrorToFormik(error, {
        setErrors,
        knownFields: CONTACT_FIELDS,
        genericMessage: t('errors.unexpected', 'Something went wrong. Please try again.'),
      });
      setNonFieldMessages(messages);
      if (messages.length > 0) {
        dispatch(addToast({ message: t('warehouse.contactSaveError', 'Failed to save contact'), level: 'error' }));
      }
      setSubmitting(false);
    }
  };

  useEffect(() => {
    dispatch(loadContacts());
  }, [dispatch]);

  return (
    <Formik
      enableReinitialize
      validateOnChange={false}
      validateOnBlur={true}
      initialValues={initialValues}
      validationSchema={ContactSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, values, touched, errors, handleChange, setFieldValue }) => (
        <Form>
          <Box sx={{ padding: 2 }}>
            <Typography variant="h6" gutterBottom>
              {contact
                ? t('warehouse.updateContact', 'Update Contact')
                : t('warehouse.createContact', 'Create Contact')}
            </Typography>

            <FormErrorAlert messages={nonFieldMessages} sx={{ mb: 2 }} />

            <Paper sx={{ padding: 2, marginBottom: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t('warehouse.basicInformation', 'Basic Information')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="name"
                    as={TextField}
                    label={t('general.name', 'Name')}
                    fullWidth
                    size="small"
                    variant="outlined"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="email"
                    as={TextField}
                    label={t('general.email', 'Email')}
                    fullWidth
                    size="small"
                    variant="outlined"
                    required
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="phone1"
                    as={TextField}
                    label={t('warehouse.phone1', 'Phone 1')}
                    fullWidth
                    size="small"
                    variant="outlined"
                    error={touched.phone1 && Boolean(errors.phone1)}
                    helperText={touched.phone1 && errors.phone1}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="phone2"
                    as={TextField}
                    label={t('warehouse.phone2', 'Phone 2')}
                    fullWidth
                    size="small"
                    variant="outlined"
                    error={touched.phone2 && Boolean(errors.phone2)}
                    helperText={touched.phone2 && errors.phone2}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={{ padding: 2, marginBottom: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t('warehouse.addressInformation', 'Address Information')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="address"
                    as={TextField}
                    label={t('warehouse.address', 'Address')}
                    fullWidth
                    size="small"
                    variant="outlined"
                    error={touched.address && Boolean(errors.address)}
                    helperText={touched.address && errors.address}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="state"
                    as={TextField}
                    label={t('warehouse.state', 'State')}
                    fullWidth
                    size="small"
                    variant="outlined"
                    error={touched.state && Boolean(errors.state)}
                    helperText={touched.state && errors.state}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="city"
                    as={TextField}
                    label={t('warehouse.city', 'City')}
                    fullWidth
                    size="small"
                    variant="outlined"
                    error={touched.city && Boolean(errors.city)}
                    helperText={touched.city && errors.city}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="zipcode"
                    as={TextField}
                    label={t('warehouse.zipcode', 'Zipcode')}
                    fullWidth
                    size="small"
                    variant="outlined"
                    error={touched.zipcode && Boolean(errors.zipcode)}
                    helperText={touched.zipcode && errors.zipcode}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={{ padding: 2, marginBottom: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t('warehouse.additionalInformation', 'Additional Information')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name="title"
                    as={TextField}
                    label={t('general.title', 'Title')}
                    fullWidth
                    size="small"
                    variant="outlined"
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Box sx={{ mt: 4, textAlign: 'right' }}>
              <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                {contact
                  ? t('warehouse.updateContact', 'Update Contact')
                  : t('warehouse.createContact', 'Create Contact')}
              </Button>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
}

export default ContactForm;
